import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setMixSelectedLeagues, setBodySelectedLeagues, playSong } from '../../common/actions/common.actions';
import { MdOutlineChecklist, MdOutlineCheckCircle } from "react-icons/md";

const FilterView = ({ onClose, leagues, onLeagueSelection, events, mixBody }) => {
  const dispatch = useDispatch();
  const { mixSelectedLeagues, bodySelectedLeagues } = useSelector((state) => state.common);
  const [allLeaguesSelected, setAllLeaguesSelected] = useState(true);
  const isWideWindow = window.innerWidth >= 1024;

  const setSelectedLeagueData = useCallback((data) => {
    mixBody === 'maung'
      ? dispatch(setMixSelectedLeagues(data))
      : dispatch(setBodySelectedLeagues(data));
  }, [dispatch]);

  useEffect(() => {
    const selectedLeagues = mixBody === 'maung' ? mixSelectedLeagues : bodySelectedLeagues;
    if (leagues?.length && selectedLeagues?.length) {
      const leagueNames = leagues.map((l) => l.leagueName);
      const isSelectAll =
        leagueNames.every((name) => selectedLeagues.includes(name)) &&
        selectedLeagues.every((name) => leagueNames.includes(name));
      setAllLeaguesSelected(isSelectAll);
      setSelectedLeagueData(selectedLeagues);
    }
  }, [leagues]);

  const handleSongClick = () => dispatch(playSong());

  const handleCheckboxChange = (leagueName) => {
    const selectedLeagues = mixBody === 'maung' ? mixSelectedLeagues : bodySelectedLeagues;
    const updated =
      allLeaguesSelected
        ? leagues.filter((l) => l.leagueName !== leagueName).map((l) => l.leagueName)
        : selectedLeagues.includes(leagueName)
          ? selectedLeagues.filter((name) => name !== leagueName)
          : [...selectedLeagues, leagueName];
    setSelectedLeagueData(updated);
    setAllLeaguesSelected(updated.length === leagues.length);
  };

  useEffect(() => {
    const selectedLeagues = mixBody === 'maung' ? mixSelectedLeagues : bodySelectedLeagues;
    if (selectedLeagues?.length === leagues?.length) {
      setAllLeaguesSelected(true);
    }
  }, [mixSelectedLeagues, bodySelectedLeagues, leagues]);

  const handleOKClick = () => {
    const selectedLeagues = mixBody === 'maung' ? mixSelectedLeagues : bodySelectedLeagues;
    handleSongClick();
    const selection = allLeaguesSelected
      ? leagues.map((l) => l.leagueName)
      : selectedLeagues;
    onLeagueSelection(selection);
    setSelectedLeagueData(selection);
    onClose();
  };

  const handleAllSelectedChange = () => {
    const selected = allLeaguesSelected ? [] : leagues.map((l) => l.leagueName);
    setSelectedLeagueData(selected);
    setAllLeaguesSelected(!allLeaguesSelected);
  };

  return (
    <div className="h-[100dvh] overflow-auto bg-gradient-to-br from-[#EAEAEA] via-green-50 to-[#228B22]/10 relative">
      {/* Football Background Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        {[...Array(4)].map((_, i) => (
          <div
            key={`filter-line-${i}`}
            className="absolute w-full h-px bg-[#228B22] opacity-30"
            style={{ top: `${(i + 1) * 20}%` }}
          />
        ))}
      </div>

      {/* Header */}
      <div className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-gradient-to-r from-[#228B22] via-emerald-600 to-[#1F5F1F] shadow-lg backdrop-blur-sm border-b border-white/20 flex items-center justify-between ${isWideWindow ? 'max-w-7xl mx-auto' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
            <MdOutlineChecklist size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">
              League Filter
            </h2>
            <p className="text-emerald-100 text-xs">
              {leagues.length} Leagues • {events} Events
            </p>
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-6 py-2 rounded-xl bg-[#FFD700] hover:bg-[#FFA500] text-gray-900 font-semibold transition-all duration-200 shadow-lg"
          onClick={handleOKClick}
        >
          <MdOutlineCheckCircle size={18} />
          Apply
        </button>
      </div>

      {/* Select All */}
      <div className="mt-24 mx-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-[#228B22]/20 shadow-lg">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={allLeaguesSelected}
            onChange={handleAllSelectedChange}
            className="w-5 h-5 text-[#228B22] bg-white border-2 border-[#228B22] rounded focus:ring-[#FFD700] focus:ring-2"
          />
          <span className="text-base font-semibold text-gray-800">
            {allLeaguesSelected ? 'Deselect All Leagues' : 'Select All Leagues'}
          </span>
        </div>
      </div>

      {/* League List */}
      <div className="px-4 pb-6">
        <ul className="mt-4 space-y-3 overflow-y-scroll max-h-[65vh]">
          {leagues.map((league, index) => (
            <li key={league.leagueId} className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 hover:border-[#228B22]/30 transition-all duration-200">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={`league-${league.leagueId}`}
                  checked={
                    allLeaguesSelected ||
                    (mixBody === 'maung'
                      ? mixSelectedLeagues.includes(league.leagueName)
                      : bodySelectedLeagues.includes(league.leagueName))
                  }
                  onChange={() => handleCheckboxChange(league.leagueName)}
                  className="w-5 h-5 text-[#228B22] bg-white border-2 border-[#228B22] rounded focus:ring-[#FFD700] focus:ring-2"
                />
                <label
                  htmlFor={`league-${league.leagueId}`}
                  className="text-sm font-semibold text-gray-800 cursor-pointer flex-1"
                >
                  {league.leagueName}
                </label>
                <div className="w-2 h-2 bg-[#1E90FF] rounded-full animate-pulse"></div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default FilterView;
